import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Leaf, Save, AlertCircle } from "lucide-react";
import DeliveryDatePicker from "@/components/DeliveryDatePicker";
import { useOrderUnits } from "@/lib/useOrderUnits";
import { getSeasonalStatusOption, isHiddenFromStore } from "@/lib/seasonal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

export default function SeasonalOrdersPage() {
  const COMMON_UNITS = useOrderUnits();
  const [products, setProducts] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [orders, setOrders] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [storeInfo, setStoreInfo] = useState(null);
  const [multiColumnMode, setMultiColumnMode] = useState(false);
  const [columnNames, setColumnNames] = useState([]);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const userStr = localStorage.getItem("user");
      const user = userStr ? JSON.parse(userStr) : null;

      const requests = [
        axios.get(`${API}/products`, getAuthHeaders()),
        axios.get(`${API}/delivery-schedules`, getAuthHeaders()),
      ];

      if (user && user.store_id) {
        requests.push(axios.get(`${API}/stores/${user.store_id}`, getAuthHeaders()));
      }

      const responses = await Promise.all(requests);
      
      // 季節商品のみをフィルタリング
      const allProducts = responses[0].data;
      const seasonalProducts = allProducts.filter(
        (p) => p.is_seasonal && !isHiddenFromStore(p)
      );
      
      setProducts(seasonalProducts);
      setSchedules(responses[1].data);

      if (responses[2]) {
        const store = responses[2].data;
        setStoreInfo(store);
        setMultiColumnMode(store.multi_column_mode || false);
        setColumnNames(store.column_names || []);
      }

      // 初期orderデータの作成（各商品の空の注文）
      const initialOrders = {};
      seasonalProducts.forEach((product) => {
        initialOrders[product.id] = {
          quantity: "",
          unit: product.default_order_unit || "g",
          delivery_date: "",
          usage_date: "",
          breakdown: multiColumnMode && columnNames.length > 0
            ? columnNames.reduce((acc, name) => ({ ...acc, [name]: "" }), {})
            : null,
        };
      });
      setOrders(initialOrders);
    } catch (error) {
      toast.error("データの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const updateOrder = (productId, field, value) => {
    setOrders((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const userStr = localStorage.getItem("user");
      const user = userStr ? JSON.parse(userStr) : null;

      if (!user || !user.store_id) {
        toast.error("店舗情報が見つかりません");
        return;
      }

      const ordersToSave = [];
      
      Object.entries(orders).forEach(([productId, orderInfo]) => {
        const qty = parseFloat(orderInfo.quantity || 0);
        if (!isNaN(qty) && qty > 0) {
          if (!orderInfo.usage_date) {
            toast.error("使用日を入力してください");
            throw new Error("使用日が未入力です");
          }

          ordersToSave.push({
            store_id: user.store_id,
            product_id: productId,
            usage_date: orderInfo.usage_date,
            delivery_date: orderInfo.delivery_date || orderInfo.usage_date,
            quantity: qty,
            unit: orderInfo.unit || null,
            breakdown: orderInfo.breakdown || null,
          });
        }
      });

      if (ordersToSave.length === 0) {
        toast.error("保存する発注がありません");
        return;
      }

      await axios.post(`${API}/orders/bulk`, { orders: ordersToSave }, getAuthHeaders());
      toast.success("季節商品の発注を保存しました");
      
      // 保存後、注文データをリセット
      const resetOrders = {};
      products.forEach((product) => {
        resetOrders[product.id] = {
          quantity: "",
          unit: product.default_order_unit || "g",
          delivery_date: "",
          usage_date: "",
          breakdown: multiColumnMode && columnNames.length > 0
            ? columnNames.reduce((acc, name) => ({ ...acc, [name]: "" }), {})
            : null,
        };
      });
      setOrders(resetOrders);
    } catch (error) {
      if (error.message !== "使用日が未入力です") {
        toast.error("発注の保存に失敗しました");
      }
    } finally {
      setSaving(false);
    }
  };

  // 配送スケジュールを日付順にソート
  const sortedSchedules = useMemo(() => {
    return [...schedules].sort((a, b) => 
      new Date(a.delivery_date) - new Date(b.delivery_date)
    );
  }, [schedules]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-600">読み込み中...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* ヘッダー */}
      <div className="flex justify-between items-start">
        <div className="flex items-stretch gap-3">
          <div className="w-1 bg-emerald-600 rounded-sm" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
              <Leaf className="h-7 w-7 text-emerald-600" />
              季節商品の発注
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              旬の食材を選んで発注できます
            </p>
          </div>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-seasonal-orders-button"
          className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-md h-10 px-5"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? "保存中..." : "発注を保存"}
        </Button>
      </div>

      {products.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-12 text-center">
          <Leaf className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">現在、季節商品はありません</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
          <div className="space-y-4">
            {products.map((product, index) => {
              const order = orders[product.id] || {};
              const rank = storeInfo?.customer_rank || "a1";
              const price = product.prices?.[rank];
              const hasValue = order.quantity && parseFloat(order.quantity) > 0;

              return (
                <div
                  key={product.id}
                  className={`grid ${multiColumnMode ? 'grid-cols-10' : 'grid-cols-8'} gap-4 items-end p-4 border-2 rounded-md transition-colors ${
                    hasValue ? "border-emerald-300 bg-emerald-50/30" : "border-slate-200 bg-slate-50/50"
                  }`}
                >
                  {/* 商品名 */}
                  <div className="col-span-2">
                    <Label className="text-sm font-medium text-slate-700">商品名</Label>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="font-mono text-[11px] text-slate-500">{product.code}</span>
                      <span className="font-medium text-slate-800">{product.name}</span>
                      {(() => {
                        const opt = getSeasonalStatusOption(product.seasonal_status);
                        if (opt) {
                          return (
                            <span className={`shrink-0 px-1.5 py-0.5 rounded-sm font-semibold text-[10px] ${opt.pillClass}`}>
                              {opt.label}
                            </span>
                          );
                        }
                        return (
                          <span className="shrink-0 px-1.5 py-0.5 rounded-sm bg-emerald-500 text-white border border-emerald-600 font-semibold text-[10px]">
                            季節
                          </span>
                        );
                      })()}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {product.unit} / {product.estimated_weight || "—"} / ¥{price || "—"}
                    </div>
                  </div>

                  {/* 多列モード: 患者・職員・合計 */}
                  {multiColumnMode ? (
                    <>
                      <div className="col-span-1">
                        <Label className="text-sm font-medium text-blue-700">患者</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={order.breakdown?.patients || ""}
                          onChange={(e) => {
                            const patients = parseFloat(e.target.value) || 0;
                            const staff = parseFloat(order.breakdown?.staff) || 0;
                            const total = patients + staff;
                            setOrders((prev) => ({
                              ...prev,
                              [product.id]: {
                                ...prev[product.id],
                                breakdown: {
                                  ...prev[product.id]?.breakdown,
                                  patients: e.target.value,
                                  staff: prev[product.id]?.breakdown?.staff || "",
                                  total: total.toFixed(2),
                                },
                                quantity: total.toFixed(2),
                              },
                            }));
                          }}
                          placeholder="0.00"
                          className="mt-1 h-9 rounded-md bg-blue-50"
                        />
                      </div>
                      <div className="col-span-1">
                        <Label className="text-sm font-medium text-green-700">職員</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={order.breakdown?.staff || ""}
                          onChange={(e) => {
                            const staff = parseFloat(e.target.value) || 0;
                            const patients = parseFloat(order.breakdown?.patients) || 0;
                            const total = patients + staff;
                            setOrders((prev) => ({
                              ...prev,
                              [product.id]: {
                                ...prev[product.id],
                                breakdown: {
                                  ...prev[product.id]?.breakdown,
                                  patients: prev[product.id]?.breakdown?.patients || "",
                                  staff: e.target.value,
                                  total: total.toFixed(2),
                                },
                                quantity: total.toFixed(2),
                              },
                            }));
                          }}
                          placeholder="0.00"
                          className="mt-1 h-9 rounded-md bg-green-50"
                        />
                      </div>
                      <div className="col-span-1">
                        <Label className="text-sm font-medium text-slate-700">合計</Label>
                        <Input
                          type="text"
                          value={order.breakdown?.total || order.quantity || "0.00"}
                          readOnly
                          className="mt-1 h-9 rounded-md bg-slate-100 text-slate-700 font-semibold"
                        />
                      </div>
                    </>
                  ) : (
                    /* 通常モード: 数量のみ */
                    <div className="col-span-1">
                      <Label className="text-sm font-medium">数量</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={order.quantity}
                        onChange={(e) => updateOrder(product.id, "quantity", e.target.value)}
                        placeholder="0.00"
                        data-testid={`seasonal-quantity-${index}`}
                        className="mt-1 h-9 rounded-md"
                      />
                    </div>
                  )}

                  {/* 単位 */}
                  <div className="col-span-1">
                    <Label className="text-sm font-medium">単位</Label>
                    <Select
                      value={order.unit || product.default_order_unit || "g"}
                      onValueChange={(value) => updateOrder(product.id, "unit", value)}
                    >
                      <SelectTrigger className="mt-1 h-9 rounded-md">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COMMON_UNITS.map((u) => (
                          <SelectItem key={u} value={u}>
                            {u}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* 使用日 */}
                  <div className="col-span-2">
                    <Label className="text-sm font-medium">使用日分</Label>
                    <DeliveryDatePicker
                      value={order.usage_date}
                      onChange={(value) => {
                        updateOrder(product.id, "usage_date", value);
                        // 使用日と同じ日付をデフォルトの配送希望日として設定
                        if (!order.delivery_date) {
                          updateOrder(product.id, "delivery_date", value);
                        }
                      }}
                      schedules={sortedSchedules}
                      className="mt-1"
                      testId={`seasonal-usage-date-${index}`}
                    />
                  </div>

                  {/* 配送希望日 */}
                  <div className="col-span-2">
                    <Label className="text-sm font-medium">配送希望日</Label>
                    <DeliveryDatePicker
                      value={order.delivery_date || order.usage_date}
                      onChange={(value) => updateOrder(product.id, "delivery_date", value)}
                      schedules={sortedSchedules}
                      className="mt-1"
                      testId={`seasonal-delivery-date-${index}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* 注意事項 */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">ご注意</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>季節商品は期間限定のため、在庫状況により入荷できない場合があります</li>
                  <li>使用日と配送希望日を必ず選択してください</li>
                  <li>数量を入力した商品のみが発注されます</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
